import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axiosInstance from "@/config/axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRESET_COLORS = [
  { name: "Red", value: "red", bg: "bg-red-100", text: "text-red-800", border: "border-red-300" },
  { name: "Orange", value: "orange", bg: "bg-orange-100", text: "text-orange-800", border: "border-orange-300" },
  { name: "Yellow", value: "yellow", bg: "bg-yellow-100", text: "text-yellow-800", border: "border-yellow-300" },
  { name: "Green", value: "green", bg: "bg-green-100", text: "text-green-800", border: "border-green-300" },
  { name: "Blue", value: "blue", bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-300" },
  { name: "Purple", value: "purple", bg: "bg-purple-100", text: "text-purple-800", border: "border-purple-300" },
  { name: "Pink", value: "pink", bg: "bg-pink-100", text: "text-pink-800", border: "border-pink-300" },
  { name: "Gray", value: "gray", bg: "bg-gray-100", text: "text-gray-800", border: "border-gray-300" }
];

export default function AIActorEmailTags() {
  const { actorId } = useParams();
  const navigate = useNavigate();
  
  const [actor, setActor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);

  useEffect(() => {
    fetchActorDetails();
    fetchTags();
  }, [actorId]);

  const fetchActorDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/ai/actors/${actorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActor(response.data);
    } catch (error) {
      console.error("Failed to fetch actor details:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      setLoadingTags(true);
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(
        `${API}/admin/ai-email/tags/${actorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTags(response.data.tags || []);
    } catch (error) {
      console.error("Failed to fetch tags", error);
    } finally {
      setLoadingTags(false);
    }
  };

  const getColorClasses = (colorValue) => {
    const color = PRESET_COLORS.find(c => c.value === colorValue);
    return color || PRESET_COLORS[7]; // Default to gray
  };

  const handleBack = () => {
    navigate(`/ai/actors/manage/${actorId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl mx-auto p-6 min-h-screen">
      {/* Header Section */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={handleBack}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Manage
        </Button>
        
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-2">Email Tags</h1>
          {actor && (
            <p className="text-muted-foreground text-lg">
              {actor.name}
              {actor.experience_level && ` • ${actor.experience_level}`}
            </p>
          )}
        </div>
      </div>

      {/* Content - Exact copy from AIEmailSettings */}
      <Card>
        <CardHeader>
          <CardTitle>Email Tags</CardTitle>
          <CardDescription>
            Tags created by AI or manually applied to emails
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingTags ? (
            <p className="text-sm text-muted-foreground">Loading tags...</p>
          ) : (tags || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No tags created yet</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {tags.map((tag) => {
                const colorClass = getColorClasses(tag.color);
                return (
                  <div key={tag.tag_name} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Badge className={`${colorClass.bg} ${colorClass.text} border ${colorClass.border}`}>
                          {tag.tag_name}
                        </Badge>
                        <p className="text-sm text-muted-foreground mt-2">
                          {tag.count} email{tag.count !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        By {tag.created_by}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
